import { jsx as _jsx } from "react/jsx-runtime";
import MuiChip from '@mui/material/Chip';
import * as React from 'react';
import { IconButton } from '../button';
import { useMergeRefs, useOverflow } from '../hooks';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { makeEnum, withVersion } from '../utils';
export * from './chip-group';
export * from './birthday-chip';
export * from './chip-variants';
const sizes = ['small', 'medium'];
export const ChipSize = makeEnum(...sizes);
const ChipImpl = React.forwardRef((props, ref) => {
    const { color = 'default', cursor, maxWidth = 150, size = 'small', label, tooltipText, selected = false, ...muiProps } = props;
    const innerRef = useMergeRefs(ref);
    const labelRef = React.useRef(null);
    const { textOverflow } = useOverflow(labelRef, props.label);
    const isRemovable = Boolean(muiProps.onDelete);
    const isSelectable = Boolean(muiProps.onClick) && !isRemovable;
    const isActive = isSelectable && selected;
    const resolvedDisabled = isSelectable ? muiProps.disabled : undefined;
    const baseMain = useColor(color, 'main');
    const baseHover = useColor(color, 'hovered');
    const baseFocused = useColor(color, 'focused');
    const baseContrastText = useColor(color, 'contrastText');
    const colorMain = isActive ? 'components.chip.active.main' : baseMain;
    const colorHover = isActive ? 'components.chip.active.hovered' : baseHover;
    const colorFocused = isActive
        ? 'components.chip.active.focused'
        : baseFocused;
    const labelColor = isActive
        ? 'components.chip.active.contrastText'
        : baseContrastText;
    const hasInteraction = Boolean(!resolvedDisabled && (muiProps.onClick || muiProps.onDelete));
    const resolvedCursor = cursor ?? (hasInteraction ? 'pointer' : 'default');
    const deleteIcon = muiProps.onDelete ? (_jsx(IconButton, { icon: "IconClose", size: "small", tabIndex: -1, "aria-label": "delete", iconProps: { sx: { fontSize: 16 } } })) : undefined;
    return (_jsx(Tooltip, { title: tooltipText ?? props.label, bypass: tooltipText ? false : !textOverflow, children: _jsx(MuiChip, { ref: innerRef, label: typeof label === 'string' ? (label ? (_jsx(Label, { color: labelColor, ref: labelRef, children: label })) : undefined) : (label), "data-role": "chip", size: size, deleteIcon: deleteIcon, ...muiProps, disabled: resolvedDisabled, color: "default", sx: {
                backgroundColor: colorMain,
                cursor: resolvedCursor,
                borderRadius: 1,
                '.MuiChip-label': label
                    ? {
                        boxSizing: 'content-box',
                        maxWidth
                    }
                    : {
                        padding: 0
                    },
                ...(hasInteraction && {
                    '&:hover': { backgroundColor: colorHover },
                    '&.Mui-focusVisible': { backgroundColor: colorFocused },
                    '&:active': { backgroundColor: colorFocused }
                }),
                ...muiProps.sx
            } }) }));
});
export const Chip = withVersion(ChipImpl, '1.2.0');
Chip.displayName = 'Chip';
const Label = React.forwardRef((props, ref) => {
    return (_jsx(Typography, { component: "span", color: props.color, variant: "inherit", ref: ref, overflow: "hidden", textOverflow: "ellipsis", display: "block", children: props.children }));
});
function useColor(color, token) {
    switch (color) {
        case 'default':
        case 'primary':
        case 'info':
        case 'error':
        case 'warning':
        case 'success':
            return `components.chip.${color}.${token}`;
        case 'app':
        case 'mail':
        case 'message':
        case 'digital-post':
        case 'letter':
            return `components.chip.additional-colour-coding.${color}.${token}`;
        default:
            return `components.chip.default.${token}`;
    }
}
export const chipStyle = {
    MuiChip: {
        styleOverrides: {
            sizeSmall: ({ theme }) => ({
                height: 24,
                fontSize: theme.palette.components?.chip?.small?.fontSize,
                fontWeight: theme.palette.components?.chip?.small?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5)
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(0.5),
                    paddingRight: theme.spacing(0.5)
                },
                '& .MuiChip-deleteIcon': {
                    padding: theme.spacing(0.375),
                    marginLeft: 0,
                    marginRight: theme.spacing(0.25),
                    color: theme.palette.action.active
                }
            }),
            sizeMedium: ({ theme }) => ({
                height: 30,
                fontSize: theme.palette.components?.chip?.medium?.fontSize,
                fontWeight: theme.palette.components?.chip?.medium?.fontWeight,
                '& .MuiChip-icon': {
                    marginLeft: theme.spacing(1),
                    marginRight: 0
                },
                '& .MuiChip-label': {
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1)
                },
                '& .MuiChip-deleteIcon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(0.5),
                    color: theme.palette.action.active
                }
            })
        }
    }
};
